import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import logo from '../../ZIPLOGYX-Logo-Link.png';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm]       = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const user = await login(form.email, form.password);
      toast.success(`Welcome back, ${user.name}!`);
      // Route via the app root, which lands each role on the right page (landingFor).
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-zip-bg">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="w-full max-w-[480px]"
      >
        <div
          className="bg-zip-surface border border-zip-border rounded-2xl overflow-hidden"
          style={{ boxShadow: '0 0 0 1px rgba(0,0,0,0.05), 0 4px 32px rgba(0,0,0,0.08)' }}
        >
          {/* Logo area */}
          <div className="relative flex flex-col items-center pt-10 pb-7 px-8">
            {/* Red radial glow */}
            <div
              className="absolute inset-x-0 top-0 h-32 pointer-events-none"
              style={{
                background: 'radial-gradient(circle at 50% 0%, rgba(232,25,44,0.07) 0%, transparent 60%)',
              }}
            />
            <img src={logo} alt="Ziplogyx" className="relative h-16 w-auto object-contain" />
            {/* Decorative red line */}
            <div className="mt-5 w-20 h-px bg-zip-red opacity-60" />
            <p className="text-zip-muted text-sm mt-3 tracking-wide">Inventory Management System</p>
          </div>

          {/* Form */}
          <div className="px-8 pb-8">
            <h2 className="text-zip-text text-lg font-semibold mb-6 tracking-tight">
              Sign in to your account
            </h2>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zip-red/10 border border-zip-red/30 text-zip-red rounded-lg px-4 py-3 text-sm mb-5"
              >
                {error}
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zip-muted mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zip-muted" />
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="zip-input pl-9"
                    placeholder="you@ziplogyx.com"
                    required
                    style={{ boxShadow: 'none' }}
                    onFocus={(e) => { e.target.style.boxShadow = '0 0 0 3px rgba(232,25,44,0.15)'; }}
                    onBlur={(e)  => { e.target.style.boxShadow = 'none'; }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zip-muted mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zip-muted" />
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="zip-input pl-9 pr-10"
                    placeholder="••••••••"
                    required
                    style={{ boxShadow: 'none' }}
                    onFocus={(e) => { e.target.style.boxShadow = '0 0 0 3px rgba(232,25,44,0.15)'; }}
                    onBlur={(e)  => { e.target.style.boxShadow = 'none'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zip-muted hover:text-zip-text transition-colors"
                    aria-label={showPass ? 'Hide password' : 'Show password'}
                  >
                    {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-2.5 mt-2 flex items-center justify-center gap-2"
                style={!loading ? { boxShadow: 'none' } : {}}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.boxShadow = '0 4px 16px rgba(232,25,44,0.35)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in…
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <p className="text-center text-xs text-zip-muted mt-6">
              Contact your administrator to get access
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
